import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";
import { confirmAlert, errorAlert, successAlert, toastMessageAlert } from "../../utils/alert";

const normalizeClassificationToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const requiresGuideRunnerForClassification = (classification) => {
  const token = normalizeClassificationToken(classification);
  if (!token) return false;
  if (/^(B|B1|B2|B3)$/.test(token)) return true;
  if (/^(T1[123]|F1[123]|S1[123]|SB1[123]|SM1[123]|PTVI|VI|ARVI)$/.test(token)) return true;
  if (token.includes("BLIND") || token.includes("VISUAL")) return true;
  return false;
};

export default function GameEngine() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [classificationCounts, setClassificationCounts] = useState({});

  const [eventId, setEventId] = useState("");
  const [gameId, setGameId] = useState("");
  const [classificationId, setClassificationId] = useState("");
  const [combineCategories, setCombineCategories] = useState(false);
  const [mergeClassificationIds, setMergeClassificationIds] = useState([]);
  const [includeUnclassified, setIncludeUnclassified] = useState(false);
  const [ageCategory, setAgeCategory] = useState("all");
  const [genderFilters, setGenderFilters] = useState({
    male: true,
    female: true,
  });
  const [componentType, setComponentType] = useState("lanes");
  const [componentCount, setComponentCount] = useState(8);
  const [componentEnabled, setComponentEnabled] = useState({
    tracks: false,
    throwing_pits: false,
    jumping_pits: false,
    courts: false,
    platforms: false,
    play_areas: false,
    lanes: false,
    others: false,
  });
  const [componentCounts, setComponentCounts] = useState({
    tracks: 8,
    throwing_pits: 2,
    jumping_pits: 2,
    courts: 2,
    platforms: 2,
    play_areas: 1,
    lanes: 8,
    others: 1,
  });
  const [scoreSheetAttemptCount, setScoreSheetAttemptCount] = useState(3);
  const [candidates, setCandidates] = useState([]);
  const [manualByReg, setManualByReg] = useState({});
  const [generatedScoreSheets, setGeneratedScoreSheets] = useState([]);
  const [loadingGeneratedScoreSheets, setLoadingGeneratedScoreSheets] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [message, setMessage] = useState("");
  const [visibilityVersion, setVisibilityVersion] = useState(0);
  const [fixtureTeamCount, setFixtureTeamCount] = useState(0);
  const [loadingFixtureTeamCount, setLoadingFixtureTeamCount] = useState(false);
  const restoredScoreSheetSelectionKeysRef = useRef(new Set());
  const dateFromQuery = searchParams.get("date") || "";
  const gameIdFromQuery = searchParams.get("game_id") || "";

  const effectiveClassificationIds = useMemo(() => {
    if (combineCategories) {
      return Array.from(new Set(mergeClassificationIds.map((v) => String(v)).filter(Boolean)));
    }
    return classificationId ? [String(classificationId)] : [];
  }, [combineCategories, mergeClassificationIds, classificationId]);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(eventId)) || null,
    [events, eventId]
  );
  const selectedGame = useMemo(
    () => games.find((g) => String(g.id) === String(gameId)) || null,
    [games, gameId]
  );
  const activeGender = useMemo(() => {
    const active = Object.entries(genderFilters)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
    return active.length === 1 ? active[0] : "all";
  }, [genderFilters]);
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (dateFromQuery) q.set("date", dateFromQuery);
    if (eventId) q.set("event_id", String(eventId));
    q.set("gender", activeGender);
    return q.toString();
  }, [dateFromQuery, eventId, activeGender]);
  const teamManagerContextQuery = useMemo(() => {
    const q = new URLSearchParams(gameDayContextQuery);
    q.set("source", "game-engine");
    if (gameId && String(gameId) !== "all") {
      q.set("game_id", String(gameId));
    }
    return q.toString();
  }, [gameDayContextQuery, gameId]);
  const isAllGamesSelected = String(gameId) === "all";
  const scoreSheetSelectionStorageKey = useMemo(() => {
    const eventToken = String(eventId || "").trim();
    const gameToken = String(gameId || "").trim();
    if (!eventToken || !gameToken || gameToken === "all") return "";
    return `game_engine_score_sheet_selection_${eventToken}_${gameToken}`;
  }, [eventId, gameId]);
  const normalizedGameType = useMemo(
    () => String(selectedGame?.game_type || "").toUpperCase().replace(/\s+/g, "_"),
    [selectedGame]
  );
  const gameName = useMemo(
    () => String(selectedGame?.game_name || "").toLowerCase(),
    [selectedGame]
  );
  const gameNarration = useMemo(
    () => String(selectedGame?.game_narration || "").toLowerCase(),
    [selectedGame]
  );
  const sportName = useMemo(
    () => String(selectedEvent?.sport_name || selectedEvent?.sport || "").toLowerCase(),
    [selectedEvent]
  );
  const gameIdentity = useMemo(
    () => `${gameName} ${gameNarration} ${sportName}`.trim(),
    [gameName, gameNarration, sportName]
  );
  const isFixtureMode = useMemo(() => {
    const relayLikeToken = /(relay|4x)/.test(`${gameName} ${gameNarration}`);
    if (!normalizedGameType) return relayLikeToken ? true : null;
    return relayLikeToken || ["BRACKET", "KNOCKOUT", "LEAGUE", "TEAM", "MATCH", "ROUND"].some((t) =>
      normalizedGameType.includes(t)
    );
  }, [normalizedGameType, gameName, gameNarration]);
  const relayLike = useMemo(() => {
    return /(relay|4x)/.test(`${gameName} ${gameNarration}`);
  }, [gameName, gameNarration]);
  const teamBracketLike = useMemo(() => {
    if (!selectedGame) return false;
    if (relayLike) return true;
    if (normalizedGameType.includes("TEAM_SPORT")) return true;
    if (normalizedGameType.includes("TEAM")) return true;
    if (!normalizedGameType.includes("BRACKET")) return false;
    return /(doubles|pairs|mixed|team)/.test(`${gameName} ${gameNarration}`);
  }, [selectedGame, relayLike, normalizedGameType, gameName, gameNarration]);
  const heatLikeByType = useMemo(() => {
    if (!selectedGame) return false;
    if (normalizedGameType.includes("TIMED")) return true;
    if (normalizedGameType.includes("TRACK")) return true;
    if (["POINTS", "DISTANCE", "DISTANCE_HEIGHT", "MAX_WEIGHT"].some((t) => normalizedGameType.includes(t))) {
      return true;
    }
    return false;
  }, [selectedGame, normalizedGameType]);
  const heatLikeByName = useMemo(() => {
    return /(swim|freestyle|backstroke|breaststroke|butterfly|medley|sprint|run|race|time trial|pursuit|\b\d+m\b|long jump|high jump|triple jump|shot put|discus|javelin|throw|archery|powerlift|weight)/.test(
      gameIdentity
    );
  }, [gameIdentity]);
  const showFixtureActions = useMemo(() => {
    if (!eventId || !gameId) return false;
    if (isAllGamesSelected) return true;
    if (!selectedGame) return false;
    return isFixtureMode === true;
  }, [eventId, gameId, isAllGamesSelected, selectedGame, isFixtureMode]);
  const requiresTeamsForFixtureSheet = useMemo(
    () => showFixtureActions && !isAllGamesSelected && teamBracketLike,
    [showFixtureActions, isAllGamesSelected, teamBracketLike]
  );
  const canUseFixtureSheetDownloads = useMemo(
    () => !requiresTeamsForFixtureSheet || fixtureTeamCount >= 2,
    [requiresTeamsForFixtureSheet, fixtureTeamCount]
  );
  const showHeatActions = useMemo(() => {
    if (!eventId || !gameId) return false;
    if (isAllGamesSelected) return true;
    if (!selectedGame) return false;
    if (showFixtureActions && !heatLikeByType && !heatLikeByName) return false;
    return true;
  }, [eventId, gameId, isAllGamesSelected, selectedGame, showFixtureActions, heatLikeByType, heatLikeByName]);
  const engineMode = useMemo(() => {
    if (!eventId || !gameId || (!selectedGame && !isAllGamesSelected)) return "unselected";
    if (showHeatActions && showFixtureActions) return "hybrid";
    if (showFixtureActions) return "fixture";
    if (showHeatActions) return "heat";
    return "unselected";
  }, [eventId, gameId, selectedGame, isAllGamesSelected, showHeatActions, showFixtureActions]);
  const modeLabel = useMemo(() => {
    if (engineMode === "fixture") return "Fixture Mode";
    if (engineMode === "heat") return "Heat/Attempts Mode";
    if (engineMode === "hybrid") return "Hybrid Mode";
    return "Select Event + Game";
  }, [engineMode]);
  const modeHint = useMemo(() => {
    if (engineMode === "fixture") {
      return "Use fixture flow only: generate fixtures, then enter winners/scores in results pages.";
    }
    if (engineMode === "heat") {
      return "Use heat/attempt flow: generate heats or manual allocations, then enter timed/points results.";
    }
    if (engineMode === "hybrid") {
      return "This selection supports both flows. Use the action that matches your operational plan.";
    }
    return "Pick event and game to auto-select the right workflow.";
  }, [engineMode]);
  const isAthleticsEvent = useMemo(() => {
    const sport = String(selectedEvent?.sport_name || selectedEvent?.sport || "").toLowerCase();
    const title = String(selectedEvent?.title || "").toLowerCase();
    return sport.includes("athlet") || title.includes("athlet");
  }, [selectedEvent]);
  const scoreSheetParams = useMemo(() => {
    return {
      attempt_count: Number(scoreSheetAttemptCount) || 3,
      combine_categories: combineCategories ? 1 : 0,
      classification_id: !combineCategories && classificationId ? Number(classificationId) : undefined,
      classification_ids:
        combineCategories && effectiveClassificationIds.length
          ? effectiveClassificationIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : undefined,
      include_unclassified: combineCategories ? (includeUnclassified ? 1 : 0) : undefined,
    };
  }, [scoreSheetAttemptCount, combineCategories, classificationId, effectiveClassificationIds, includeUnclassified]);
  const generatedCombinedSheets = useMemo(() => {
    return (Array.isArray(generatedScoreSheets) ? generatedScoreSheets : []).filter(
      (row) => String(row?.mode || "").toLowerCase() === "combined"
    );
  }, [generatedScoreSheets]);
  const generatedIndividualSheets = useMemo(() => {
    return (Array.isArray(generatedScoreSheets) ? generatedScoreSheets : []).filter(
      (row) => {
        const mode = String(row?.mode || "").toLowerCase();
        return mode !== "combined" && mode !== "fixture";
      }
    );
  }, [generatedScoreSheets]);
  const generatedFixtureSheets = useMemo(() => {
    return (Array.isArray(generatedScoreSheets) ? generatedScoreSheets : []).filter(
      (row) => String(row?.mode || "").toLowerCase() === "fixture"
    );
  }, [generatedScoreSheets]);
  const isGuideRunnerRuleRow = (row) =>
    !!row?.guide_runner_required || requiresGuideRunnerForClassification(row?.classification);
  const formatGeneratedSheetDate = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return "-";
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return text;
    return parsed.toLocaleString();
  };
  const generatedSheetClassificationText = (row) => {
    const names = (Array.isArray(row?.classification_names) ? row.classification_names : [])
      .map((name) => String(name || "").trim())
      .filter(Boolean);
    const ids = (Array.isArray(row?.classification_ids) ? row.classification_ids : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const includeMissed = Boolean(row?.include_unclassified);
    const text = names.length
      ? names.join(" + ")
      : (ids.length ? ids.map((id) => `Class ${id}`).join(" + ") : "");
    if (text && includeMissed) return `${text} + Missed / New Player`;
    if (text) return text;
    if (includeMissed) return "Missed / New Player";
    return "All Classifications";
  };
  const generatedSheetParams = (row) => {
    const modeToken = String(row?.mode || "").toLowerCase();
    const mode =
      modeToken === "combined" ? "combined" : (modeToken === "fixture" ? "fixture" : "individual");
    const ids = Array.from(
      new Set(
        (Array.isArray(row?.classification_ids) ? row.classification_ids : [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );
    const genderToken = String(row?.gender || "").toLowerCase();
    return {
      attempt_count: Math.max(1, Math.min(12, Number(row?.attempt_count) || 3)),
      combine_categories: mode === "combined" ? 1 : 0,
      classification_id: mode === "individual" && ids.length === 1 ? ids[0] : undefined,
      classification_ids: ids.length ? ids : undefined,
      include_unclassified: row?.include_unclassified ? 1 : undefined,
      gender: genderToken === "male" || genderToken === "female" ? genderToken : "all",
      game_id: row?.game_id ? Number(row.game_id) : "all",
      registration_ids: undefined,
    };
  };
  const extractRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };
  const getClassificationCountLabel = (classificationId) => {
    const key = String(classificationId ?? "");
    if (!Object.prototype.hasOwnProperty.call(classificationCounts, key)) return "-";
    const value = Number(classificationCounts[key]);
    return Number.isFinite(value) ? String(value) : "-";
  };
  const generatedSheetCategoryCountText = (row) => {
    const list = Array.isArray(row?.category_player_counts) ? row.category_player_counts : [];
    if (!list.length) return "-";
    return list
      .map((item) => {
        const id = Number(item?.classification_id || 0);
        const label = String(item?.classification || "").trim() || (id > 0 ? `Class ${id}` : "Missed / New Player");
        const count = Number(item?.player_count ?? 0);
        return `${label}: ${Number.isFinite(count) ? count : 0}`;
      })
      .join(", ");
  };
  const generatedSheetTotalPlayers = (row) => {
    const explicit = Number(row?.total_players);
    if (Number.isFinite(explicit) && explicit >= 0) return explicit;
    const list = Array.isArray(row?.category_player_counts) ? row.category_player_counts : [];
    const fallback = list.reduce((sum, item) => sum + (Number(item?.player_count) || 0), 0);
    return fallback > 0 ? fallback : "-";
  };
  const hasClassificationCounts = useMemo(
    () => Object.keys(classificationCounts || {}).length > 0,
    [classificationCounts]
  );
  const visibleClassifications = useMemo(() => {
    if (!eventId || !gameId) return classifications;
    if (!hasClassificationCounts) return classifications;
    return classifications.filter((c) => Number(classificationCounts[String(c.id)] || 0) > 0);
  }, [classifications, classificationCounts, eventId, gameId, hasClassificationCounts]);
  const hasUnclassifiedCount = Object.prototype.hasOwnProperty.call(classificationCounts, "0");
  const visibleUnclassifiedCount = hasUnclassifiedCount ? Number(classificationCounts["0"] || 0) : null;
  const classificationById = useMemo(() => {
    const map = new Map();
    (Array.isArray(classifications) ? classifications : []).forEach((c) => {
      map.set(String(c.id), c);
    });
    return map;
  }, [classifications]);
  const selectableClassifications = useMemo(() => {
    const list = (Array.isArray(visibleClassifications) ? visibleClassifications : []).map((c) => ({
      ...c,
      __hiddenLocked: false,
    }));
    const selectedId = String(classificationId || "").trim();
    if (!selectedId) return list;
    const exists = list.some((c) => String(c.id) === selectedId);
    if (exists) return list;
    const fallback = classificationById.get(selectedId);
    list.push({
      id: selectedId,
      classification: fallback?.classification || `Class ${selectedId}`,
      __hiddenLocked: true,
    });
    return list;
  }, [visibleClassifications, classificationById, classificationId]);
  const selectableMergeClassifications = useMemo(() => {
    const list = (Array.isArray(visibleClassifications) ? visibleClassifications : []).map((c) => ({
      ...c,
      __hiddenLocked: false,
    }));
    const existing = new Set(list.map((c) => String(c.id)));
    (Array.isArray(mergeClassificationIds) ? mergeClassificationIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean)
      .forEach((id) => {
        if (existing.has(id)) return;
        const fallback = classificationById.get(id);
        list.push({
          id,
          classification: fallback?.classification || `Class ${id}`,
          __hiddenLocked: true,
        });
        existing.add(id);
      });
    return list;
  }, [visibleClassifications, mergeClassificationIds, classificationById]);

  const componentPreset = useMemo(() => {
    const game = String(selectedGame?.game_name || "").toLowerCase();
    const sport = String(selectedEvent?.sport_name || selectedEvent?.sport || "").toLowerCase();
    const pick = (type, count, label) => ({ type, count, label });

    if (game.includes("long jump") || game.includes("high jump") || game.includes("triple jump")) {
      return pick("jumping_pits", 2, "Jumping Pits");
    }
    if (game.includes("shot put") || game.includes("discus") || game.includes("javelin") || game.includes("throw")) {
      return pick("throwing_pits", 2, "Throwing Pits");
    }
    if (sport.includes("athlet") || game.includes("track") || game.includes("100m") || game.includes("200m") || game.includes("400m")) {
      return pick("tracks", 8, "Tracks/Lanes");
    }
    if (sport.includes("swim")) {
      return pick("lanes", 8, "Pool Lanes");
    }
    if (sport.includes("badminton") || sport.includes("tennis") || sport.includes("basketball") || sport.includes("volleyball")) {
      return pick("courts", 2, "Courts");
    }
    if (sport.includes("powerlifting")) {
      return pick("platforms", 2, "Platforms");
    }
    return pick("play_areas", 1, "Play Areas");
  }, [selectedGame, selectedEvent]);

  const componentOptions = useMemo(
    () => [
      { value: "tracks", label: "Tracks / Lanes" },
      { value: "throwing_pits", label: "Throwing Pits" },
      { value: "jumping_pits", label: "Jumping Pits" },
      { value: "courts", label: "Courts" },
      { value: "platforms", label: "Platforms" },
      { value: "play_areas", label: "Play Areas" },
      { value: "lanes", label: "Lanes" },
      { value: "others", label: "Others" },
    ],
    []
  );

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await API.fetchAdminEvents();
        const list = res?.data || res || [];
        setEvents(Array.isArray(list) ? list : []);

        const eventFromQuery = searchParams.get("event_id");
        if (eventFromQuery) setEventId(eventFromQuery);
        const genderFromQuery = (searchParams.get("gender") || "").toLowerCase();
        if (genderFromQuery === "male" || genderFromQuery === "men") {
          setGenderFilters({ male: true, female: false });
        } else if (genderFromQuery === "female" || genderFromQuery === "women") {
          setGenderFilters({ male: false, female: true });
        } else if (genderFromQuery === "all") {
          setGenderFilters({ male: true, female: true });
        }
      } catch (err) {
        setEvents([]);
        setMessage(err?.message || "Failed to load events.");
      }
    };

    loadEvents();
  }, [searchParams]);

  useEffect(() => {
    const loadDependentData = async () => {
      if (!selectedEvent) {
        setGames([]);
        setClassifications([]);
        setGameId("");
        setClassificationId("");
        return;
      }

      try {
        const sportId = Number(selectedEvent?.sport_id || 0);
        const [eventGamesResult, classResult] = await Promise.allSettled([
          API.get(`/admin/game-engine/events/${selectedEvent.id}/games`),
          sportId
            ? API.get(`/admin/classifications/sports/${sportId}/classifications`)
            : Promise.resolve({ data: [] }),
        ]);

        let gamesList =
          eventGamesResult.status === "fulfilled"
            ? (eventGamesResult.value?.data || eventGamesResult.value || [])
            : [];
        if ((!Array.isArray(gamesList) || gamesList.length === 0) && sportId > 0) {
          try {
            const fallbackGames = await API.fetchGamesBySport(sportId);
            gamesList = fallbackGames?.data || fallbackGames || [];
          } catch (_fallbackErr) {
            // keep empty list, handled below
          }
        }

        const classList =
          classResult.status === "fulfilled"
            ? (classResult.value?.data || classResult.value || [])
            : [];
        const normalizedGames = Array.isArray(gamesList) ? gamesList : [];

        setGames(normalizedGames);
        setClassifications(Array.isArray(classList) ? classList : []);
        setGameId((prev) => {
          const prevValue = String(prev || "");
          const isValidGame = (value) =>
            value === "all" ||
            (value && normalizedGames.some((g) => String(g.id) === String(value)));
          if (isValidGame(prevValue)) return prevValue;
          if (isValidGame(gameIdFromQuery)) return String(gameIdFromQuery);
          return "";
        });
        setClassificationId("");
        setCombineCategories(false);
        setMergeClassificationIds([]);
        setIncludeUnclassified(false);
        setAgeCategory("all");
        setCandidates([]);
        setManualByReg({});

        if (eventGamesResult.status === "rejected" && classResult.status === "rejected") {
          setMessage("Failed to load games/classifications.");
        }
      } catch (err) {
        setGames([]);
        setClassifications([]);
        setMessage(err?.message || "Failed to load games/classifications.");
      }
    };

    loadDependentData();
  }, [selectedEvent, gameIdFromQuery]);

  useEffect(() => {
    if (!gameIdFromQuery) return;
    const isValid =
      gameIdFromQuery === "all" ||
      games.some((g) => String(g.id) === String(gameIdFromQuery));
    if (!isValid) return;
    setGameId((prev) =>
      String(prev || "") === String(gameIdFromQuery) ? prev : String(gameIdFromQuery)
    );
  }, [gameIdFromQuery, games]);

  useEffect(() => {
    const loadClassificationCounts = async () => {
      if (!eventId || !gameId) {
        setClassificationCounts({});
        return;
      }

      try {
        const genders = Object.entries(genderFilters)
          .filter(([, enabled]) => enabled)
          .map(([key]) => key);
        const res = await API.get("/admin/game-engine/classification-counts", {
          params: {
            event_id: eventId,
            game_id: gameId,
            age_category: ageCategory,
            component_type: componentType,
            genders,
          },
        });
        const rows = extractRows(res);
        const next = {};
        rows.forEach((row) => {
          const rawId = row?.classification_id;
          if (rawId === undefined || rawId === null) return;
          const id = String(rawId);
          next[id] = Number(row?.applied_count ?? row?.player_count ?? row?.count ?? 0);
        });
        setClassificationCounts(next);
      } catch (_err) {
        setClassificationCounts({});
      }
    };

    loadClassificationCounts();
  }, [eventId, gameId, ageCategory, componentType, genderFilters, visibilityVersion]);

  useEffect(() => {
    const loadGeneratedScoreSheets = async () => {
      if (!eventId) {
        setGeneratedScoreSheets([]);
        setLoadingGeneratedScoreSheets(false);
        return;
      }
      setLoadingGeneratedScoreSheets(true);
      try {
        const res = await API.get("/admin/game-engine/generated-score-sheets", {
          params: {
            event_id: Number(eventId),
            game_id: gameId && !isAllGamesSelected ? Number(gameId) : undefined,
          },
        });
        const rows = extractRows(res);
        setGeneratedScoreSheets(rows);
      } catch (_err) {
        setGeneratedScoreSheets([]);
      } finally {
        setLoadingGeneratedScoreSheets(false);
      }
    };

    loadGeneratedScoreSheets();
  }, [eventId, gameId, isAllGamesSelected, visibilityVersion]);

  useEffect(() => {
    if (!selectedGame) return;
    setComponentType(componentPreset.type);
    setComponentCount(componentPreset.count);
    setComponentEnabled((prev) => ({ ...prev, [componentPreset.type]: true }));
    setComponentCounts((prev) => ({ ...prev, [componentPreset.type]: componentPreset.count }));
  }, [selectedGame, componentPreset]);

  useEffect(() => {
    if (!scoreSheetSelectionStorageKey) return;
    if (!selectedEvent) return;
    if (!selectedGame) return;
    if (restoredScoreSheetSelectionKeysRef.current.has(scoreSheetSelectionStorageKey)) return;

    try {
      const raw = window.localStorage.getItem(scoreSheetSelectionStorageKey);
      if (!raw) {
        restoredScoreSheetSelectionKeysRef.current.add(scoreSheetSelectionStorageKey);
        return;
      }
      const parsed = JSON.parse(raw);
      const savedCombine = Boolean(parsed?.combineCategories);
      const savedSingle = String(parsed?.classificationId || "").trim();
      const savedMerge = Array.isArray(parsed?.mergeClassificationIds)
        ? Array.from(
            new Set(
              parsed.mergeClassificationIds
                .map((id) => String(id || "").trim())
                .filter(Boolean)
            )
          )
        : [];
      const savedIncludeUnclassified = Boolean(parsed?.includeUnclassified);

      if (savedCombine) {
        setCombineCategories(true);
        setClassificationId("");
        setMergeClassificationIds(savedMerge);
        setIncludeUnclassified(savedIncludeUnclassified);
      } else {
        setCombineCategories(false);
        setClassificationId(savedSingle);
        setMergeClassificationIds([]);
        setIncludeUnclassified(false);
      }
    } catch (_err) {
      // Ignore invalid/blocked local storage.
    } finally {
      restoredScoreSheetSelectionKeysRef.current.add(scoreSheetSelectionStorageKey);
    }
  }, [scoreSheetSelectionStorageKey, selectedEvent, selectedGame]);

  useEffect(() => {
    if (!scoreSheetSelectionStorageKey) return;
    if (!restoredScoreSheetSelectionKeysRef.current.has(scoreSheetSelectionStorageKey)) return;
    try {
      const payload = {
        combineCategories: Boolean(combineCategories),
        classificationId: String(classificationId || "").trim(),
        mergeClassificationIds: Array.from(
          new Set(
            (Array.isArray(mergeClassificationIds) ? mergeClassificationIds : [])
              .map((id) => String(id || "").trim())
              .filter(Boolean)
          )
        ),
        includeUnclassified: Boolean(includeUnclassified),
      };
      window.localStorage.setItem(scoreSheetSelectionStorageKey, JSON.stringify(payload));
    } catch (_err) {
      // Ignore local storage errors in private/incognito or blocked contexts.
    }
  }, [
    scoreSheetSelectionStorageKey,
    combineCategories,
    classificationId,
    mergeClassificationIds,
    includeUnclassified,
  ]);

  useEffect(() => {
    let alive = true;
    if (!eventId || !gameId || String(gameId) === "all" || !teamBracketLike) {
      setFixtureTeamCount(0);
      setLoadingFixtureTeamCount(false);
      return undefined;
    }

    const loadTeamCount = async () => {
      setLoadingFixtureTeamCount(true);
      try {
        const res = await API.listTeams(eventId, { game_id: Number(gameId) });
        if (!alive) return;
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setFixtureTeamCount(rows.length);
      } catch (_err) {
        if (!alive) return;
        setFixtureTeamCount(0);
      } finally {
        if (alive) setLoadingFixtureTeamCount(false);
      }
    };

    loadTeamCount();
    return () => {
      alive = false;
    };
  }, [eventId, gameId, teamBracketLike, visibilityVersion]);

  const effectiveComponentCounts = useMemo(() => {
    const out = {};
    Object.keys(componentEnabled).forEach((k) => {
      if (componentEnabled[k]) {
        out[k] = Number(componentCounts[k] || 0);
      }
    });
    return out;
  }, [componentEnabled, componentCounts]);

  const generateHeats = async () => {
    if (!eventId || !gameId) {
      setMessage("Please select event and game.");
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      const res = await API.post("/admin/game-engine/heats", {
        event_id: eventId,
        game_id: gameId,
        classification_id: combineCategories ? null : (classificationId || null),
        classification_ids: combineCategories ? effectiveClassificationIds : undefined,
        include_unclassified: combineCategories ? includeUnclassified : undefined,
        combine_categories: combineCategories,
        merge_classification_ids: combineCategories ? effectiveClassificationIds : [],
        age_category: ageCategory,
        component_type: componentType,
        component_count: Number(componentCount) || 1,
        component_counts: isAllGamesSelected ? effectiveComponentCounts : undefined,
      });
      setMessage(res?.message || "Heats generated successfully.");
    } catch (err) {
      setMessage(err?.message || "Failed to generate heats.");
    } finally {
      setBusy(false);
    }
  };

  const loadCandidates = async () => {
    if (!eventId || !gameId) {
      setMessage("Please select event and game.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const genders = Object.entries(genderFilters)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);
      if (!genders.length) {
        setMessage("Select at least one gender to load players.");
        setBusy(false);
        return;
      }
      const res = await API.get("/admin/game-engine/heat-candidates", {
        params: {
          event_id: eventId,
          game_id: gameId,
          classification_id: combineCategories ? undefined : (classificationId || undefined),
          classification_ids: combineCategories ? effectiveClassificationIds : undefined,
          include_unclassified: combineCategories ? (includeUnclassified ? 1 : 0) : undefined,
          combine_categories: combineCategories,
          merge_classification_ids: combineCategories ? effectiveClassificationIds : [],
          age_category: ageCategory,
          genders,
        },
      });
      const rows = extractRows(res);
      setCandidates(rows);
      const initial = {};
      rows.forEach((r) => {
        const key = r.candidate_key || `${r.registration_id}_${r.game_id || 0}`;
        initial[key] = { heat_number: "", lane_number: "" };
      });
      setManualByReg(initial);
      const guideRows = rows.filter((r) => isGuideRunnerRuleRow(r)).length;
      setMessage(
        guideRows > 0
          ? `Loaded ${rows.length} players. ${guideRows} blind-category row(s) require guide-runner pairing (odd lanes only).`
          : `Loaded ${rows.length} players for manual heat assignment.`
      );
    } catch (err) {
      setMessage(err?.message || "Failed to load players.");
      setCandidates([]);
      setManualByReg({});
    } finally {
      setBusy(false);
    }
  };

  const autoFillManual = () => {
    if (!candidates.length) return;
    const next = {};
    const assignRowsWithGuideRunnerRule = (rows, rawCapacity, startHeatNo = 1) => {
      const baseCapacity = Math.max(1, Math.min(32, Number(rawCapacity) || 8));
      const grouped = new Map();
      rows.forEach((r) => {
        const classKey = String(r?.classification || "__unclassified__");
        if (!grouped.has(classKey)) grouped.set(classKey, []);
        grouped.get(classKey).push(r);
      });

      let heatCursor = startHeatNo;
      grouped.forEach((classRows, classKey) => {
        const guideRuleActive =
          classRows.some((r) => isGuideRunnerRuleRow(r)) ||
          requiresGuideRunnerForClassification(classKey === "__unclassified__" ? "" : classKey);
        const playersPerHeat = guideRuleActive
          ? Math.max(1, Math.floor(baseCapacity / 2))
          : baseCapacity;
        classRows.forEach((r, idx) => {
          const heatNo = heatCursor + Math.floor(idx / playersPerHeat);
          const slot = idx % playersPerHeat;
          const laneNo = guideRuleActive ? (slot * 2) + 1 : slot + 1;
          const key = r.candidate_key || `${r.registration_id}_${r.game_id || 0}`;
          next[key] = { heat_number: String(heatNo), lane_number: String(laneNo) };
        });
        heatCursor += Math.max(1, Math.ceil(classRows.length / playersPerHeat));
      });
    };

    const inferTypeForGame = (gameName) => {
      const game = String(gameName || "").toLowerCase();
      const sport = String(selectedEvent?.sport_name || selectedEvent?.sport || "").toLowerCase();
      if (
        sport.includes("swim") ||
        game.includes("freestyle") ||
        game.includes("backstroke") ||
        game.includes("breaststroke") ||
        game.includes("butterfly") ||
        game.includes("medley")
      ) {
        return "lanes";
      }
      if (game.includes("long jump") || game.includes("high jump") || game.includes("triple jump")) return "jumping_pits";
      if (game.includes("shot put") || game.includes("discus") || game.includes("javelin") || game.includes("throw")) return "throwing_pits";
      if (sport.includes("athlet") || game.includes("track") || game.includes("100m") || game.includes("200m") || game.includes("400m")) return "tracks";
      if (sport.includes("badminton") || sport.includes("tennis") || sport.includes("basketball") || sport.includes("volleyball")) return "courts";
      if (sport.includes("powerlifting")) return "platforms";
      return "play_areas";
    };

    if (!isAllGamesSelected) {
      assignRowsWithGuideRunnerRule(candidates, Number(componentCount) || 8, 1);
    } else {
      const byGame = {};
      candidates.forEach((r) => {
        const gid = String(r.game_id || 0);
        if (!byGame[gid]) byGame[gid] = [];
        byGame[gid].push(r);
      });

      Object.values(byGame).forEach((rows) => {
        const type = inferTypeForGame(rows[0]?.game_name);
        const configured = componentEnabled[type] ? Number(componentCounts[type] || 0) : 0;
        const cap = Math.max(1, Math.min(32, configured || Number(componentCount) || 8));
        assignRowsWithGuideRunnerRule(rows, cap, 1);
      });
    }

    setManualByReg(next);
    const guideRows = candidates.filter((r) => isGuideRunnerRuleRow(r)).length;
    setMessage(
      guideRows > 0
        ? "Manual table auto-filled. Blind-category rows use guide-runner pairing (odd lane numbers only)."
        : "Manual table auto-filled based on lane/equipment capacity."
    );
  };

  const saveManualHeats = async () => {
    if (!eventId || !gameId) {
      setMessage("Please select event and game.");
      return;
    }
    const assignments = candidates
      .map((r) => ({
        registration_id: Number(r.registration_id),
        game_id: Number(r.game_id || 0),
        heat_number: Number(
          manualByReg[r.candidate_key || `${r.registration_id}_${r.game_id || 0}`]?.heat_number || 0
        ),
        lane_number: Number(
          manualByReg[r.candidate_key || `${r.registration_id}_${r.game_id || 0}`]?.lane_number || 0
        ),
      }))
      .filter((r) =>
        r.registration_id > 0 &&
        r.heat_number > 0 &&
        r.lane_number > 0 &&
        (!isAllGamesSelected || r.game_id > 0)
      );

    if (!assignments.length) {
      setMessage("Enter heat and lane values before saving.");
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      const res = await API.post("/admin/game-engine/heats/manual", {
        event_id: Number(eventId),
        game_id: isAllGamesSelected ? "all" : Number(gameId),
        classification_id: combineCategories ? null : (classificationId || null),
        classification_ids: combineCategories ? effectiveClassificationIds : undefined,
        include_unclassified: combineCategories ? includeUnclassified : undefined,
        combine_categories: combineCategories,
        merge_classification_ids: combineCategories ? effectiveClassificationIds : [],
        age_category: ageCategory,
        assignments,
      });
      setMessage(res?.message || "Manual heats saved successfully.");
    } catch (err) {
      setMessage(err?.message || "Failed to save manual heats.");
    } finally {
      setBusy(false);
    }
  };

  const generateFixtures = async ({ clearExisting = false } = {}) => {
    if (!eventId || !gameId) {
      setMessage("Please select event and game.");
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      if (clearExisting) {
        await API.clearFixtures({
          event_id: Number(eventId),
          game_id: String(gameId) === "all" || !gameId ? "all" : Number(gameId),
        });
      }
      const res = await API.post("/admin/game-engine/fixtures", {
        event_id: eventId,
        game_id: gameId,
        gender: activeGender,
        classification_id: combineCategories ? null : (classificationId || null),
        classification_ids: combineCategories ? effectiveClassificationIds : undefined,
        include_unclassified: combineCategories ? includeUnclassified : undefined,
        combine_categories: combineCategories,
        merge_classification_ids: combineCategories ? effectiveClassificationIds : [],
        age_category: ageCategory,
      });
      setMessage(res?.message || "Fixtures generated successfully.");
      setVisibilityVersion((v) => v + 1);
    } catch (err) {
      setMessage(err?.message || "Failed to generate fixtures.");
    } finally {
      setBusy(false);
    }
  };

  const clearFixtures = async () => {
    if (!eventId) {
      setMessage("Please select event.");
      return;
    }

    const target = String(gameId) === "all" || !gameId ? "all games in this event" : "selected game";
    const confirm = await confirmAlert({
      title: "Clear Fixtures?",
      text: `This will remove existing fixtures for ${target}.`,
      confirmText: "Clear",
      cancelText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setBusy(true);
    setMessage("");
    try {
      const payload = {
        event_id: Number(eventId),
        game_id: String(gameId) === "all" || !gameId ? "all" : Number(gameId),
      };
      const res = await API.clearFixtures(payload);
      setMessage(res?.message || "Fixtures cleared.");
      successAlert("Cleared", res?.message || "Fixtures cleared.");
      setVisibilityVersion((v) => v + 1);
    } catch (err) {
      const msg = err?.message || "Failed to clear fixtures.";
      setMessage(msg);
      errorAlert("Clear Failed", msg);
    } finally {
      setBusy(false);
    }
  };

  const runRecommendedSetup = async () => {
    if (engineMode === "fixture") {
      await generateFixtures({ clearExisting: true });
      return;
    }
    if (engineMode === "heat") {
      await generateHeats();
      return;
    }
    if (engineMode === "hybrid") {
      setMessage("This game supports both Heats and Fixtures. Choose the needed action below.");
      return;
    }
    setMessage("Select event and game first.");
  };

  const clearHeats = async () => {
    if (!eventId) {
      setMessage("Please select event.");
      return;
    }

    const target = String(gameId) === "all" || !gameId ? "all games in this event" : "selected game";
    const confirm = await confirmAlert({
      title: "Clear Heats?",
      text: `This will remove existing heats for ${target}.`,
      confirmText: "Clear",
      cancelText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setBusy(true);
    setMessage("");
    try {
      const payload = {
        event_id: Number(eventId),
        game_id: String(gameId) === "all" || !gameId ? "all" : Number(gameId),
      };
      const res = await API.clearHeats(payload);
      setCandidates([]);
      setManualByReg({});
      setMessage(res?.message || "Heats cleared.");
      successAlert("Cleared", res?.message || "Heats cleared.");
      setVisibilityVersion((v) => v + 1);
    } catch (err) {
      const msg = err?.message || "Failed to clear heats.";
      setMessage(msg);
      errorAlert("Clear Failed", msg);
    } finally {
      setBusy(false);
    }
  };

  const resetResultSheetLocks = async () => {
    if (!eventId) {
      setMessage("Please select event.");
      return;
    }

    const target = String(gameId) === "all" || !gameId ? "all games in this event" : "selected game";
    const confirm = await confirmAlert({
      title: "Reset Generated Categories?",
      text: `This will show hidden categories/players again for ${target}.`,
      confirmText: "Reset",
      cancelText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setBusy(true);
    setMessage("");
    try {
      const payload = {
        event_id: Number(eventId),
        game_id: String(gameId) === "all" || !gameId ? "all" : Number(gameId),
      };
      const res = await API.post("/admin/game-engine/result-sheet/reset", payload);
      setCandidates([]);
      setManualByReg({});
      setVisibilityVersion((v) => v + 1);
      setMessage(res?.message || "Result-sheet locks reset.");
      successAlert("Reset Done", res?.message || "Result-sheet locks reset.");
    } catch (err) {
      const msg = err?.message || "Failed to reset result-sheet locks.";
      setMessage(msg);
      errorAlert("Reset Failed", msg);
    } finally {
      setBusy(false);
    }
  };

  const downloadGameDayReport = async (path, fallbackName, format = "pdf", extraParams = {}) => {
    if (!eventId) {
      setMessage("Please select event before downloading reports.");
      return;
    }

    const gender = activeGender;

    setDownloadingReport(true);
    setMessage("");
    try {
      const params = {
        gender,
        format,
        game_id: !isAllGamesSelected && gameId ? Number(gameId) : undefined,
        ...extraParams,
      };
      const res = await API.get(path, { params, responseType: "blob" });
      const blob = res?.data;
      if (!blob) throw new Error("Empty file response.");

      const disposition =
        res?.headers?.["content-disposition"] ||
        res?.headers?.["Content-Disposition"] ||
        "";
      const contentType =
        (
          res?.headers?.["content-type"] ||
          res?.headers?.["Content-Type"] ||
          blob?.type ||
          ""
        ).toLowerCase();

      let fileName = fallbackName;
      const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
      if (match?.[1]) fileName = decodeURIComponent(match[1]);
      if (format === "excel" && contentType.includes("text/csv")) {
        if (/\.xlsx$/i.test(fileName)) {
          fileName = fileName.replace(/\.xlsx$/i, ".csv");
        } else if (!/\.csv$/i.test(fileName)) {
          fileName = `${fileName}.csv`;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (String(path).includes("/score-sheet/")) {
        setCandidates([]);
        setManualByReg({});
        setVisibilityVersion((v) => v + 1);
      }
      setMessage(`${format.toUpperCase()} report downloaded.`);
    } catch (err) {
      setMessage(err?.message || "Failed to download report.");
    } finally {
      setDownloadingReport(false);
    }
  };

  const printGameDayReport = async (path, extraParams = {}) => {
    if (!eventId) {
      setMessage("Please select event before printing reports.");
      return;
    }

    const gender = activeGender;

    setDownloadingReport(true);
    setMessage("");
    try {
      const visibleRegistrationIds = Array.from(
        new Set(
          candidates
            .map((row) => Number(row?.registration_id))
            .filter((id) => Number.isFinite(id) && id > 0)
        )
      );
      const hasSpecificGame = Boolean(gameId) && !isAllGamesSelected;
      const hasSpecificClassification = !combineCategories && Boolean(classificationId);
      const useLoadedPlayersForPrint = hasSpecificGame && hasSpecificClassification;
      const params = {
        gender,
        format: "print",
        game_id: !isAllGamesSelected && gameId ? Number(gameId) : undefined,
        registration_ids:
          useLoadedPlayersForPrint && visibleRegistrationIds.length ? visibleRegistrationIds : undefined,
        ...extraParams,
      };
      const res = await API.get(path, { params, responseType: "text" });
      const html =
        typeof res === "string"
          ? res
          : (typeof res?.data === "string" ? res.data : "");
      if (!html.trim()) throw new Error("Empty print response.");

      const popup = window.open("", "_blank");
      if (!popup) {
        setMessage("Popup blocked. Please allow popups and try again.");
        return;
      }

      popup.document.open();
      popup.document.write(html);
      popup.document.close();

      const triggerPrint = () => {
        try {
          popup.focus();
          popup.print();
        } catch (_err) {
          setMessage("Opened print page. Use Ctrl+P to print.");
        }
      };
      popup.onload = () => {
        setTimeout(triggerPrint, 220);
      };
      setTimeout(triggerPrint, 900);

      if (String(path).includes("/score-sheet/")) {
        setCandidates([]);
        setManualByReg({});
        setVisibilityVersion((v) => v + 1);
      }
      setMessage("Print dialog opened.");
    } catch (err) {
      setMessage(err?.message || "Failed to print report.");
    } finally {
      setDownloadingReport(false);
    }
  };

  const printManualLoadSheet = () => {
    if (!candidates.length) {
      setMessage("Load players first to print manual sheet.");
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const rowsHtml = candidates
      .map((r, idx) => {
        const key = r.candidate_key || `${r.registration_id}_${r.game_id || 0}`;
        const heat = manualByReg[key]?.heat_number ?? "";
        const lane = manualByReg[key]?.lane_number ?? "";
        return `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(r.player_name || "")}</td>
            <td>${escapeHtml(r.gender || "-")}</td>
            <td>${escapeHtml(r.age_category || "-")}</td>
            <td>${escapeHtml(r.classification || "New Player")}</td>
            <td>${escapeHtml(r.district || "-")}</td>
            <td>${escapeHtml(r.game_name || "-")}</td>
            <td>${escapeHtml(heat)}</td>
            <td>${escapeHtml(lane)}</td>
          </tr>
        `;
      })
      .join("");

    const titleEvent = selectedEvent?.title || `Event #${eventId || "-"}`;
    const titleGame = selectedGame?.game_name || (isAllGamesSelected ? "All Games" : "-");

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Manual Heat Sheet</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 18px; color: #111827; }
            h1 { margin: 0 0 8px; font-size: 18px; }
            .meta { margin: 0 0 12px; font-size: 12px; color: #374151; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #d1d5db; padding: 6px; text-align: left; vertical-align: top; }
            thead th { background: #f3f4f6; }
            @media print {
              body { padding: 0; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          <h1>Manual Heat Assignment Sheet</h1>
          <p class="meta">
            Event: ${escapeHtml(titleEvent)} |
            Game: ${escapeHtml(titleGame)} |
            Players: ${candidates.length}
          </p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Gender</th>
                <th>Age Category</th>
                <th>Classification</th>
                <th>District</th>
                <th>Game</th>
                <th>Heat No</th>
                <th>Lane / Equip</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    iframe.contentWindow.focus();
    
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
    }, 250);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto p-6">
          <div className="flex items-start justify-between gap-3 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Game Engine</h1>
              <p className="text-gray-600">
                Select event and game. Engine mode is auto-selected so operators see only relevant workflow actions.
              </p>
            </div>
            <button
              onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
              className="text-sm px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
            >
              Back to Game Day
            </button>
          </div>

          <div className="bg-white rounded-xl shadow p-5">
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
                <SearchableSelect
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5"
                >
                  <option value="">-- Select Event --</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Game</label>
                <SearchableSelect
                  value={gameId}
                  onChange={(e) => setGameId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5"
                  disabled={!eventId}
                >
                  <option value="all">All Games</option>
                  <option value="">-- Select Game --</option>
                  {games.map((game) => (
                    <option key={game.id} value={game.id}>
                      {game.game_name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classification (Optional)</label>
                <SearchableSelect
                  value={classificationId}
                  onChange={(e) => setClassificationId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5"
                  disabled={!eventId}
                >
                  <option value="">-- All / Not Specific --</option>
                  {selectableClassifications.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.classification}
                      {` (${getClassificationCountLabel(c.id)})`}
                      {c.__hiddenLocked ? " [Generated-Hidden]" : ""}
                    </option>
                  ))}
                </SearchableSelect>
                <label className="flex items-center gap-2 mt-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={combineCategories}
                    onChange={(e) => {
                      setCombineCategories(e.target.checked);
                      if (!e.target.checked) {
                        setMergeClassificationIds([]);
                        setIncludeUnclassified(false);
                      }
                    }}
                  />
                  Multiple Classifications (with Count)
                </label>
                {combineCategories ? (
                  <p className="mt-1 text-[11px] text-gray-500">
                    In this mode, selection is taken from the checklist below.
                  </p>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age Category</label>
                <SearchableSelect
                  value={ageCategory}
                  onChange={(e) => setAgeCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5"
                >
                  <option value="all">All</option>
                  <option value="sub_junior">Sub-Junior (&le;14)</option>
                  <option value="junior">Junior (15-17)</option>
                  <option value="senior">Senior (18+)</option>
                </SearchableSelect>
                <div className="mt-2 flex items-center gap-3 text-xs text-gray-700">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={genderFilters.male}
                      onChange={(e) =>
                        setGenderFilters((prev) => ({ ...prev, male: e.target.checked }))
                      }
                    />
                    Male
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={genderFilters.female}
                      onChange={(e) =>
                        setGenderFilters((prev) => ({ ...prev, female: e.target.checked }))
                      }
                    />
                    Female
                  </label>
                </div>
              </div>

              {showHeatActions ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Component Type</label>
                    <SearchableSelect
                      value={componentType}
                      onChange={(e) => setComponentType(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-2.5"
                      disabled={isAllGamesSelected}
                    >
                      {componentOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Available Components</label>
                    <input
                      type="number"
                      min="1"
                      max="32"
                      value={componentCount}
                      onChange={(e) => setComponentCount(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-2.5"
                      disabled={isAllGamesSelected}
                    />
                    <p className="text-xs text-gray-500 mt-1">Suggested: {componentPreset.label} ({componentPreset.count})</p>
                  </div>
                </>
              ) : null}
            </div>

            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-sm font-semibold text-slate-900">Workflow Mode: {modeLabel}</p>
              <p className="text-xs text-slate-600 mt-1">{modeHint}</p>
            </div>

            {isAllGamesSelected && showHeatActions ? (
              <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                <p className="text-sm font-medium text-gray-800 mb-2">
                  All Games Resource Inputs (check and set available counts)
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  {componentOptions.map((o) => (
                    <div key={o.value} className="flex items-center gap-2 border rounded bg-white px-2 py-1">
                      <input
                        type="checkbox"
                        checked={componentEnabled[o.value]}
                        onChange={(e) =>
                          setComponentEnabled((prev) => ({ ...prev, [o.value]: e.target.checked }))
                        }
                      />
                      <span className="text-xs flex-1">{o.label}</span>
                      <input
                        type="number"
                        min="1"
                        max="32"
                        value={componentCounts[o.value]}
                        onChange={(e) =>
                          setComponentCounts((prev) => ({ ...prev, [o.value]: e.target.value }))
                        }
                        disabled={!componentEnabled[o.value]}
                        className="w-16 border rounded px-1 py-0.5 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {combineCategories ? (
              <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                <p className="text-sm font-medium text-gray-800 mb-2">
                  Select classifications to include (counts shown)
                </p>
                {(Number(visibleUnclassifiedCount || 0) > 0) || includeUnclassified || hasUnclassifiedCount ? (
                  <div className="mb-2">
                    <label className="flex items-center gap-2 border rounded px-2 py-1 bg-white text-sm">
                      <input
                        type="checkbox"
                        checked={includeUnclassified}
                        onChange={(e) => setIncludeUnclassified(e.target.checked)}
                      />
                      <span>
                        Missed / New Player (No Classification) ({visibleUnclassifiedCount ?? "-"})
                        {hasUnclassifiedCount && Number(visibleUnclassifiedCount || 0) === 0 ? " [Generated-Hidden]" : ""}
                      </span>
                    </label>
                  </div>
                ) : null}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                  {selectableMergeClassifications
                    .map((c) => {
                      const checked = mergeClassificationIds.includes(String(c.id));
                      const count = getClassificationCountLabel(c.id);
                      return (
                        <label key={c.id} className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const id = String(c.id);
                              setMergeClassificationIds((prev) =>
                                e.target.checked
                                  ? Array.from(new Set([...prev, id]))
                                  : prev.filter((x) => x !== id)
                              );
                            }}
                          />
                          <span>
                            {c.classification} ({count})
                            {c.__hiddenLocked ? " [Generated-Hidden]" : ""}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3 mt-5">
              {engineMode === "fixture" || engineMode === "heat" ? (
                <button
                  onClick={runRecommendedSetup}
                  disabled={busy || !eventId || !gameId}
                  className={`text-white px-5 py-2.5 rounded-lg disabled:bg-gray-400 ${
                    engineMode === "fixture"
                      ? "bg-emerald-700 hover:bg-emerald-800"
                      : "bg-blue-700 hover:bg-blue-800"
                  }`}
                >
                  {engineMode === "fixture" ? "Run Setup (Clear + Generate Fixtures)" : "Run Setup (Generate Heats)"}
                </button>
              ) : null}

              {showFixtureActions && !isAllGamesSelected && teamBracketLike ? (
                <button
                  onClick={() => navigate(`/admin/results/team/${eventId}?${teamManagerContextQuery}`)}
                  disabled={!eventId || !gameId}
                  className="bg-cyan-700 text-white px-5 py-2.5 rounded-lg hover:bg-cyan-800 disabled:bg-gray-400"
                >
                  {relayLike ? "Select Relay Team Players" : "Manage Teams"}
                </button>
              ) : null}
              {showHeatActions ? (
                <>
                  <button
                    onClick={generateHeats}
                    disabled={busy || !eventId || !gameId}
                    className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    Generate Heats
                  </button>
                  <button
                    onClick={clearHeats}
                    disabled={busy || !eventId}
                    className="bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                  >
                    Clear Heats
                  </button>
                  <button
                    onClick={resetResultSheetLocks}
                    disabled={busy || !eventId}
                    className="bg-rose-700 text-white px-5 py-2.5 rounded-lg hover:bg-rose-800 disabled:bg-gray-400"
                  >
                    Reset Generated Categories
                  </button>
                  <button
                    onClick={loadCandidates}
                    disabled={busy || !eventId || !gameId}
                    className="bg-slate-700 text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 disabled:bg-gray-400"
                  >
                    Load Players (Manual)
                  </button>
                  <button
                    onClick={printManualLoadSheet}
                    disabled={busy || !candidates.length}
                    className="bg-cyan-700 text-white px-5 py-2.5 rounded-lg hover:bg-cyan-800 disabled:bg-gray-400"
                  >
                    Print Manual Sheet
                  </button>
                  <button
                    onClick={autoFillManual}
                    disabled={busy || !candidates.length}
                    className="bg-violet-600 text-white px-5 py-2.5 rounded-lg hover:bg-violet-700 disabled:bg-gray-400"
                  >
                    Auto Fill Manual
                  </button>
                  <button
                    onClick={saveManualHeats}
                    disabled={busy || !candidates.length}
                    className="bg-amber-600 text-white px-5 py-2.5 rounded-lg hover:bg-amber-700 disabled:bg-gray-400"
                  >
                    Save Manual Heats
                  </button>
                </>
              ) : null}

              {showFixtureActions ? (
                <>
                  <button
                    onClick={() => generateFixtures({ clearExisting: false })}
                    disabled={busy || !eventId || !gameId}
                    className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
                  >
                    Generate Fixtures (No Clear)
                  </button>
                  <button
                    onClick={clearFixtures}
                    disabled={busy || !eventId}
                    className="bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                  >
                    Clear Fixtures
                  </button>
                </>
              ) : null}
            </div>

            <div className="mt-4 border-t pt-4">
              <p className="text-sm font-medium text-gray-800 mb-2">Downloads (current selection)</p>
              {showHeatActions ? (
                <div className="mb-3 flex flex-wrap items-end gap-2">
                  <label className="text-xs text-gray-700">
                    Score Sheet Attempts
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={scoreSheetAttemptCount}
                      onChange={(e) => setScoreSheetAttemptCount(e.target.value)}
                      className="ml-2 w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {showHeatActions ? (
                  <>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/score-sheet/${eventId}`,
                          `score_sheet_event_${eventId}.pdf`,
                          "pdf",
                          scoreSheetParams
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:bg-gray-400"
                    >
                      Score Sheet PDF
                    </button>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/score-sheet/${eventId}`,
                          `score_sheet_event_${eventId}.xlsx`,
                          "excel",
                          scoreSheetParams
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-black disabled:bg-gray-400"
                    >
                      Score Sheet Excel
                    </button>
                    <button
                      onClick={() =>
                        printGameDayReport(
                          `/admin/game-day/score-sheet/${eventId}`,
                          scoreSheetParams
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:bg-gray-400"
                    >
                      Print Score Sheet
                    </button>
                  </>
                ) : null}
                {showFixtureActions && !isAllGamesSelected ? (
                  <>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/score-sheet/${eventId}`,
                          `match_sheet_event_${eventId}_game_${gameId}.pdf`,
                          "pdf",
                          { game_id: Number(gameId), gender: activeGender }
                        )
                      }
                      disabled={!eventId || !gameId || downloadingReport || loadingFixtureTeamCount || !canUseFixtureSheetDownloads}
                      className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:bg-gray-400"
                    >
                      Match Sheet PDF
                    </button>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/score-sheet/${eventId}`,
                          `match_sheet_event_${eventId}_game_${gameId}.xlsx`,
                          "excel",
                          { game_id: Number(gameId), gender: activeGender }
                        )
                      }
                      disabled={!eventId || !gameId || downloadingReport || loadingFixtureTeamCount || !canUseFixtureSheetDownloads}
                      className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-black disabled:bg-gray-400"
                    >
                      Match Sheet Excel
                    </button>
                    <button
                      onClick={() =>
                        printGameDayReport(
                          `/admin/game-day/score-sheet/${eventId}`,
                          { game_id: Number(gameId), gender: activeGender }
                        )
                      }
                      disabled={!eventId || !gameId || downloadingReport || loadingFixtureTeamCount || !canUseFixtureSheetDownloads}
                      className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:bg-gray-400"
                    >
                      Print Match Sheet
                    </button>
                  </>
                ) : null}
                {showFixtureActions && !isAllGamesSelected ? (
                  <button
                    onClick={() =>
                      navigate(
                        `/admin/results/bracket/${eventId}?${teamManagerContextQuery}`
                      )
                    }
                    disabled={!eventId || !gameId}
                    className="bg-cyan-700 text-white px-4 py-2 rounded-lg hover:bg-cyan-800 disabled:bg-gray-400"
                  >
                    Manual Fixtures (Singles/Doubles/Mixed)
                  </button>
                ) : null}
                {showHeatActions ? (
                  <>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/heat-schedule/${eventId}`,
                          `heat_schedule_event_${eventId}.pdf`,
                          "pdf"
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-sky-700 text-white px-4 py-2 rounded-lg hover:bg-sky-800 disabled:bg-gray-400"
                    >
                      Heat Schedule PDF
                    </button>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/heat-schedule/${eventId}`,
                          `heat_schedule_event_${eventId}.xlsx`,
                          "excel"
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-sky-900 text-white px-4 py-2 rounded-lg hover:bg-sky-950 disabled:bg-gray-400"
                    >
                      Heat Schedule Excel
                    </button>
                  </>
                ) : null}
                <button
                  onClick={() =>
                    downloadGameDayReport(
                      `/admin/game-day/attendance-sheet/${eventId}`,
                      `attendance_sheet_event_${eventId}.pdf`,
                      "pdf"
                    )
                  }
                  disabled={!eventId || downloadingReport}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  Attendance PDF
                </button>
                <button
                  onClick={() =>
                    downloadGameDayReport(
                      `/admin/game-day/attendance-sheet/${eventId}`,
                      `attendance_sheet_event_${eventId}.xlsx`,
                      "excel"
                    )
                  }
                  disabled={!eventId || downloadingReport}
                  className="bg-indigo-800 text-white px-4 py-2 rounded-lg hover:bg-indigo-900 disabled:bg-gray-400"
                >
                  Attendance Excel
                </button>
                <button
                  onClick={() =>
                    downloadGameDayReport(
                      `/admin/game-day/event-list/${eventId}`,
                      `event_list_${eventId}.pdf`,
                      "pdf"
                    )
                  }
                  disabled={!eventId || downloadingReport}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
                >
                  Event List PDF
                </button>
                <button
                  onClick={() =>
                    downloadGameDayReport(
                      `/admin/game-day/event-list/${eventId}`,
                      `event_list_${eventId}.xlsx`,
                      "excel"
                    )
                  }
                  disabled={!eventId || downloadingReport}
                  className="bg-emerald-800 text-white px-4 py-2 rounded-lg hover:bg-emerald-900 disabled:bg-gray-400"
                >
                  Event List Excel
                </button>
                {isAthleticsEvent ? (
                  <>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/athletics-bib-cards/${eventId}`,
                          `athletics_bib_cards_event_${eventId}.pdf`,
                          "pdf"
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 disabled:bg-gray-400"
                    >
                      BIB Cards PDF
                    </button>
                    <button
                      onClick={() =>
                        downloadGameDayReport(
                          `/admin/game-day/athletics-bib-cards/${eventId}`,
                          `athletics_bib_cards_event_${eventId}.xlsx`,
                          "excel"
                        )
                      }
                      disabled={!eventId || downloadingReport}
                      className="bg-amber-800 text-white px-4 py-2 rounded-lg hover:bg-amber-900 disabled:bg-gray-400"
                    >
                      BIB Cards Excel
                    </button>
                  </>
                ) : null}
              </div>
              {showFixtureActions && !showHeatActions ? (
                <p className="mt-3 text-xs text-gray-600">
                  This game uses fixture/bracket workflow. Match sheets are generated from fixtures.
                </p>
              ) : null}
              {requiresTeamsForFixtureSheet && !canUseFixtureSheetDownloads ? (
                <p className="mt-2 text-xs text-amber-700">
                  Create at least 2 teams for this game before using Match Sheet PDF/Excel/Print.
                </p>
              ) : null}
            </div>

            {message ? (
              <p className="mt-4 text-sm text-gray-700 bg-gray-100 rounded-lg p-3">{message}</p>
            ) : null}

            {showHeatActions && candidates.length ? (
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2 w-14 text-center">S.No</th>
                      <th className="border p-2">Player</th>
                      <th className="border p-2">Age</th>
                      <th className="border p-2">Age Group</th>
                      <th className="border p-2">Gender</th>
                      <th className="border p-2">District</th>
                      <th className="border p-2">Classification</th>
                      <th className="border p-2">Game Opted</th>
                      <th className="border p-2">Heat No</th>
                      <th className="border p-2">Lane / Equip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((r, idx) => {
                      const key = r.candidate_key || `${r.registration_id}_${r.game_id || 0}`;
                      const guideRuleActive = isGuideRunnerRuleRow(r);
                      return (
                      <tr key={key}>
                        <td className="border p-2 text-center">{idx + 1}</td>
                        <td className="border p-2">{r.player_name}</td>
                        <td className="border p-2">{r.age || "-"}</td>
                        <td className="border p-2">{r.age_category || "-"}</td>
                        <td className="border p-2">{r.gender || "-"}</td>
                        <td className="border p-2">{r.district || "-"}</td>
                        <td className="border p-2">
                          {r.classification || "New Player"}
                          {guideRuleActive ? (
                            <div className="text-[11px] text-amber-700">Guide runner required</div>
                          ) : null}
                        </td>
                        <td className="border p-2">{r.game_name || "-"}</td>
                        <td className="border p-2">
                          <input
                            type="number"
                            min="1"
                            value={manualByReg[key]?.heat_number ?? ""}
                            onChange={(e) =>
                              setManualByReg((prev) => ({
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  heat_number: e.target.value,
                                },
                              }))
                            }
                            className="w-24 border border-gray-300 rounded px-2 py-1"
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            min="1"
                            max="32"
                            step={guideRuleActive ? 2 : 1}
                            value={manualByReg[key]?.lane_number ?? ""}
                            onChange={(e) =>
                              setManualByReg((prev) => ({
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  lane_number: e.target.value,
                                },
                              }))
                            }
                            className="w-24 border border-gray-300 rounded px-2 py-1"
                          />
                          {guideRuleActive ? (
                            <div className="text-[11px] text-amber-700 mt-1">Use odd lanes only</div>
                          ) : null}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          {(showHeatActions || showFixtureActions) && loadingGeneratedScoreSheets ? (
            <p className="mt-4 text-sm text-center text-gray-600">Loading previously generated score sheets...</p>
          ) : (showHeatActions || showFixtureActions) ? (
          <div className="mt-6 bg-white rounded-xl shadow p-5">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Generated Score Sheets</h2>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Individual Category Score Sheets</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2 w-14 text-center">S.No</th>
                      <th className="border p-2">Date</th>
                      <th className="border p-2">Game</th>
                      <th className="border p-2">Classification</th>
                      <th className="border p-2">Category-wise Players</th>
                      <th className="border p-2">Total Players</th>
                      <th className="border p-2">Attempts</th>
                      <th className="border p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedIndividualSheets.map((row, idx) => (
                        <tr key={row.id}>
                          <td className="border p-2 text-center">{idx + 1}</td>
                          <td className="border p-2">{formatGeneratedSheetDate(row.created_at)}</td>
                          <td className="border p-2">{row.game_name || "All Games"}</td>
                          <td className="border p-2">{generatedSheetClassificationText(row)}</td>
                          <td className="border p-2">{generatedSheetCategoryCountText(row)}</td>
                          <td className="border p-2 text-center">{generatedSheetTotalPlayers(row)}</td>
                          <td className="border p-2">{row.attempt_count || "-"}</td>
                          <td className="border p-2">
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  downloadGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    `score_sheet_${row.id}.pdf`,
                                    "pdf",
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-slate-600 text-white hover:bg-slate-700"
                              >
                                PDF
                              </button>
                              <button
                                onClick={() =>
                                  downloadGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    `score_sheet_${row.id}.xlsx`,
                                    "excel",
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-slate-800 text-white hover:bg-slate-900"
                              >
                                Excel
                              </button>
                              <button
                                onClick={() =>
                                  printGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-gray-600 text-white hover:bg-gray-700"
                              >
                                Print
                              </button>
                            </div>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Combined Category Score Sheets</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2 w-14 text-center">S.No</th>
                      <th className="border p-2">Date</th>
                      <th className="border p-2">Game</th>
                      <th className="border p-2">Classification</th>
                      <th className="border p-2">Category-wise Players</th>
                      <th className="border p-2">Total Players</th>
                      <th className="border p-2">Attempts</th>
                      <th className="border p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedCombinedSheets.map((row, idx) => (
                        <tr key={row.id}>
                          <td className="border p-2 text-center">{idx + 1}</td>
                          <td className="border p-2">{formatGeneratedSheetDate(row.created_at)}</td>
                          <td className="border p-2">{row.game_name || "All Games"}</td>
                          <td className="border p-2">{generatedSheetClassificationText(row)}</td>
                          <td className="border p-2">{generatedSheetCategoryCountText(row)}</td>
                          <td className="border p-2 text-center">{generatedSheetTotalPlayers(row)}</td>
                          <td className="border p-2">{row.attempt_count || "-"}</td>
                          <td className="border p-2">
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  downloadGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    `score_sheet_${row.id}.pdf`,
                                    "pdf",
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-slate-600 text-white hover:bg-slate-700"
                              >
                                PDF
                              </button>
                              <button
                                onClick={() =>
                                  downloadGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    `score_sheet_${row.id}.xlsx`,
                                    "excel",
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-slate-800 text-white hover:bg-slate-900"
                              >
                                Excel
                              </button>
                              <button
                                onClick={() =>
                                  printGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-gray-600 text-white hover:bg-gray-700"
                              >
                                Print
                              </button>
                            </div>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {generatedFixtureSheets.length ? (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Fixture Match Score Sheets</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 w-14 text-center">S.No</th>
                        <th className="border p-2">Date</th>
                        <th className="border p-2">Game</th>
                        <th className="border p-2">Gender</th>
                        <th className="border p-2">Sets</th>
                        <th className="border p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedFixtureSheets.map((row, idx) => (
                        <tr key={row.id}>
                          <td className="border p-2 text-center">{idx + 1}</td>
                          <td className="border p-2">{formatGeneratedSheetDate(row.created_at)}</td>
                          <td className="border p-2">{row.game_name || "All Games"}</td>
                          <td className="border p-2 capitalize">{row.gender || "all"}</td>
                          <td className="border p-2 text-center">{row.attempt_count || "-"}</td>
                          <td className="border p-2">
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  downloadGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    `match_sheet_${row.id}.pdf`,
                                    "pdf",
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-slate-600 text-white hover:bg-slate-700"
                              >
                                PDF
                              </button>
                              <button
                                onClick={() =>
                                  downloadGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    `match_sheet_${row.id}.xlsx`,
                                    "excel",
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-slate-800 text-white hover:bg-slate-900"
                              >
                                Excel
                              </button>
                              <button
                                onClick={() =>
                                  printGameDayReport(
                                    `/admin/game-day/score-sheet/${row.event_id}`,
                                    generatedSheetParams(row)
                                  )
                                }
                                className="text-xs px-2 py-1 rounded bg-gray-600 text-white hover:bg-gray-700"
                              >
                                Print
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
